// Customizable Area Start
import React from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Grid,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import * as Yup from "yup";
import { bsLogo, sideLoginImage, mobileBGimage } from "./assets";
import NavigationMenu from "../../../blocks/navigationmenu/src/NavigationMenu.web";
import AppFooter from "../../../components/src/AppFooter.web";
import NavButton from "../../../components/src/NavButton";
import FullPageLoader from "../../../components/src/FullPageLoader.web";
import CustomInfoDialog from "../../../components/src/CustomInfoDialog.web";
import TwoFAConfirmation from "../../otp-input-confirmation/src/TwoFAConfirmation.web";
import GoogleAd from "../../../components/src/GoogleAd.web";
const configJSON = require("./config.js");

export const validationSchema = Yup.object({
  email: Yup.string()
    .transform((value) => value?.trim())
    .email("Invalid email format")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid email format!"
    )
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
});
// Customizable Area End

import EmailAccountLoginController, {
  Props,
} from "./EmailAccountLoginController";

export default class EmailAccountLoginBlock extends EmailAccountLoginController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }
  // Customizable Area Start

  // Customizable Area End
  render() {
    const {
      isLoading,
      isSessionInfoDialog
    } = this.state
    return (
      // Customizable Area Start
      // Required for all blocks
      <>
        <NavigationMenu navigation={this.props.navigation} id={""} />
        <FullPageLoader isLoading={isLoading} />
        <Box>
          <Grid
            container
            sx={{
              overflow: "hidden",
              backgroundColor: "rgb(239, 246, 255);",
              "@media (max-width: 900px)": {
                backgroundImage: `url('${mobileBGimage}')`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
              },
            }}
          >
            {/* Left Side - Login Form (80%) */}
            <Grid
              item
              xs={12}
              md={8}
              lg={8}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                sx={{
                  width: "500px",
                  minHeight: "637px",
                  borderRadius: "20px",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  position: "relative", // Add relative positioning for inner padding
                  marginTop: "91px",
                  marginBottom: "92px",
                  backgroundColor: "white",
                  padding: "50px",
                  "@media (max-width: 600px)": {
                    marginLeft: "24px",
                    marginRight: "24px",
                    minHeight: "710px",
                    padding: "30px",
                  },
                }}
              >
                <Box sx={{ overflow: "hidden" }}>
                  <Box
                    component="img"
                    src={bsLogo}
                    alt="bsLogo"
                    sx={modalStyle.logoBox}
                  />
                  <Box>
                    <Box>
                      <Typography
                        data-test-id="main-heading"
                        variant="h5"
                        fontWeight="bold"
                        display="block"
                        sx={modalStyle.heading}
                        gutterBottom
                      >
                        Welcome to Bharat Stage
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={modalStyle.loginText}
                        mb={2}
                      >
                        Please enter your log in details
                      </Typography>
                    </Box>
                  </Box>

                  {/* Formik Wrapper */}
                  <Formik
                    data-test-id="formik"
                    initialValues={{ email: "", password: "" }}
                    validationSchema={validationSchema}
                    onSubmit={this.handleEmailLogin}
                    validateOnChange={false}
                    validateOnBlur={false}
                  >
                    {({ errors, values, setFieldValue, handleSubmit }) => (
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSubmit();
                        }}
                      >
                        {/* Email Input */}
                        <Typography sx={modalStyle.inputText}>
                          Email Address
                        </Typography>
                        <Field
                          test-data-id="input-email"
                          as={TextField}
                          fullWidth
                          name="email"
                          type="email"
                          inputMode="email"
                          variant="outlined"
                          margin="normal"
                          placeholder="Enter your email address"
                          value={values.email}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            this.setEmail(e.target.value);
                            setFieldValue("email", e.target.value);
                          }}
                          sx={{ margin: "0px" }}
                          error={Boolean(errors.email)}
                          InputProps={{
                            sx: {
                              borderRadius: "25px",
                              backgroundColor: "white",
                              height: "56px",
                            },
                          }}
                          helperText={
                            errors.email ? (
                              <span className="errorText">{errors.email}</span>
                            ) : (
                              ""
                            )
                          }
                        />

                        {/* Password Input */}
                        <Typography
                          sx={{
                            ...modalStyle.inputText,
                            marginTop: "20px !important",
                          }}
                        >
                          Password
                        </Typography>
                        <Field
                          as={TextField}
                          fullWidth
                          type={this.state.showPassword ? "text" : "password"}
                          variant="outlined"
                          name="password"
                          margin="normal"
                          placeholder="Enter your password"
                          value={values.password}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            this.setPassword(e.target.value);
                            setFieldValue("password", e.target.value);
                          }}
                          sx={{ margin: "0px" }}
                          error={Boolean(errors.password)}
                          helperText={
                            errors.password ? (
                              <span className="errorText">
                                {errors.password}
                              </span>
                            ) : (
                              ""
                            )
                          }
                          InputProps={{
                            sx: {
                              borderRadius: "25px",
                              backgroundColor: "white",
                              height: "56px",
                              font: "50px",
                            },
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ marginRight: "5px" }}
                              >
                                <IconButton
                                  data-test-id="password-visibility"
                                  onClick={this.togglePasswordVisibility}
                                  edge="end"
                                >
                                  {this.state.showPassword ? (
                                    <VisibilityOutlinedIcon
                                      sx={{ color: "#94A3B8" }}
                                    />
                                  ) : (
                                    <VisibilityOffOutlinedIcon
                                      sx={{ color: "#94A3B8" }}
                                    />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />

                        <Box textAlign="right">
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={modalStyle.forgotPassword}
                            onClick={this.handleForgotPassRedirect}
                          >
                            Forgot Password?
                          </Typography>
                        </Box>

                        <Button
                          test-data-id="btnEmailLogIn"
                          fullWidth
                          type="submit"
                          variant="contained"
                          disabled={isLoading}
                          sx={{ ...modalStyle.submitButton }}
                        >
                          Log In
                        </Button>
                        {this.state.loginErrors.map((error) => (
                          <Box
                            key={error}
                            data-test-id="login-error"
                            sx={{
                              color: "red",
                              textAlign: "left",
                              fontFamily: "Montserrat",
                              fontWeight: 400,
                              fontSize: "12px",
                              lineHeight: "16px",
                              marginBottom: "8px",
                            }}
                          >
                            {error}
                          </Box>
                        ))}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            marginTop: "20px",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              height: "1px",
                              backgroundColor: "#CBD5E1",
                              width: "35%",
                            }}
                          />
                          <Typography
                            sx={{
                              mx: 2, // Margin on both sides
                              fontFamily: "Montserrat",
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: "24px",
                              color: "#64748B",
                            }}
                          >
                            or
                          </Typography>
                          <Box
                            sx={{
                              height: "1px",
                              backgroundColor: "#CBD5E1",
                              width: "35%",
                            }}
                          />
                        </Box>

                        <Typography
                          variant="body2"
                          mt={1}
                          sx={modalStyle.donthaveaccount}
                        >
                          Don't have an account?
                          <NavButton
                            dataTestId="signupBtn"
                            classname={""}
                            path="EmailAccountRegistration"
                            onClick={this.handleNavigation}
                          >
                            Sign Up
                          </NavButton>
                        </Typography>
                      </Form>
                    )}
                  </Formik>
                </Box>
              </Box>
            </Grid>

            {/* Right Side - Static Image (20%) */}
            <Grid
              item
              xs={12}
              md={4}
              lg={4}
              sx={{
                "@media (max-width: 900px)": {
                  display: "none",
                },
                bgcolor: "#F5F5F5",
                padding: 0,
                backgroundColor: "rgb(239, 246, 255);",
              }}
            >
              <Box sx={{ width: "100%", height: "100%", textAlign: "end" }}>
                <img
                  src={sideLoginImage}
                  alt="Static Side Image"
                  style={{
                    padding: "0px",
                    margin: "0px",
                    borderRadius: "10px",
                    height: "calc(100% + 11%)",
                    marginTop: "-10%",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <GoogleAd responsive format="auto" sx={{ mt: 6 }} slot="4058033225" />
        <AppFooter navigation={this.props.navigation} />

        <TwoFAConfirmation
          data-test-id="two-fa-confirmation"
          navigation={this.props.navigation}
          isOpen={this.state.is2FAVerificationDialog}
          phoneNumber={this.state.TwoFALoginPhoneNumber}
          handleClose={this.handleClose2FADialog}
          handleFullPageLoader={this.handleFullPageLoader}
          handleSuccessPhoneVerified={this.handleSuccessPhoneVerified}
        />

        <CustomInfoDialog
          infoType="success"
          isOpen={this.state.isInfoDialogOpen}
          title={configJSON.successfulLoginText}
          handleClose={this.handleCloseInfoDialog}
        />

        <CustomInfoDialog
          infoType="error"
          isOpen={isSessionInfoDialog}
          title={configJSON.sessionExpiredMessage}
          handleClose={this.handleCloseSessionInfoDialog}
        />
      </>
      // Customizable Area End
    );
  }
}
// Customizable Area Start
const modalStyle = {
  logoBox: {
    width: "147.67px",
    height: "100px",
    display: "none",
    "@media (max-width: 490px)": {
      display: "block",
      marginBottom: "28px",
    },
    marginBottom: "28px",
  },
  heading: {
    fontFamily: "Montserrat",
    fontWeight: 600,
    wordWrap: "break-word",
    textOverflow: "ellipsis",
    fontSize: "28px",
    lineHeight: "32px",
    letterSpacing: "-0.12px",
    color: "#0F172A",
    marginBottom: "20px",
    textAlign: "left",
    "@media (max-width: 528px)": {
      marginBottom: "16px",
      wordWrap: "break-word",
      textOverflow: "ellipsis",
      whiteSpace: "normal",
      textAlign: "left",
      height: "70px",
      fontFamily: "Montserrat",
      fontWeight: 600,
      fontSize: "36px",
      lineHeight: "36px",
    },
  },
  loginText: {
    fontFamily: "Montserrat",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "19.5px",
    color: "#64748B",
    height: "20px",
    PaddingTop: "20px",
    textAlign: "left",
    "@media (max-width: 490px)": {
      textAlign: "left",
      fontFamily: "Montserrat",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "19.5px",
    },
  },
  dividerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#999",
    marginY: 2,
  },
  dividerLine: {
    borderBottom: "solid 1px #CBD5E1",
    width: "140px",
    height: "0px",
  },
  dividerText: {
    marginX: 1,
  },
  inputText: {
    font: "Montserrat",
    weight: 400,
    fontSize: "14px",
    lineHeight: "22px",
    paragraphSpacing: "8px",
    color: "#475569",
    height: "22px",
    textAlign: "left",
    marginBottom: "4px",
  },
  submitButton: {
    width: "100%",
    height: "56px",
    background: "linear-gradient(90deg, #8077FF 0%, #652784 60%)",
    borderRadius: "30px",
    fontFamily: "Montserrat",
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: "24px",
    paragraphSpacing: "8px",
    margin: "10px 0px 20px",
    textTransform: "none",
    "&.Mui-disabled": {
      color: "#FFFFFF",
      opacity: 0.25,
    },
  },
  forgotPassword: {
    fontFamily: "Montserrat", // Font family
    fontWeight: 500, // Font weight (medium)
    fontSize: "14px", // Font size
    lineHeight: "22px", // Line height
    marginBottom: "6px", // Text color
    marginTop: "15px",
    color: "#652784",
    cursor: "pointer",
    "&:hover": {
      fontWeight: 600,
    },
  },
  donthaveaccount: {
    fontFamily: "Montserrat", // Font family
    fontWeight: 500, // Font weight
    fontSize: "16px", // Font size
    lineHeight: "22px", // Line height
    color: "#64748B", // Text color
    "& > button": {
      textTransform: "none",
      fontFamily: "Montserrat", // Font family
      fontWeight: 500, // Font weight
      fontSize: "16px", // Font size
      lineHeight: "22px", // Line height
    },
  },
  signUP: {
    fontFamily: "Montserrat",
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "22px",
    color: "#652784",
    marginBottom: "6px",
    cursor: "pointer",
  },
  orText: {
    fontFamily: "Montserrat", // Font family
    fontWeight: 500, // Font weight
    fontSize: "14px", // Font size
    lineHeight: "24px", // Line height
    color: "#64748B", // Text color
    marginBottom: "8px",
  },
};
// Customizable Area End
